import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class DeepSweepCartUpsells extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
    this.#promoteImagesIfOpen();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    requestAnimationFrame(() => this.#promoteImagesIfOpen());
  };

  #promoteImagesIfOpen() {
    const drawer = this.closest('#cart-drawer');
    if (!drawer?.querySelector('dialog[open]')) return;

    this.querySelectorAll('.ds-cart-upsells__image').forEach((image, index) => {
      image.loading = 'eager';
      image.fetchPriority = index === 0 ? 'high' : 'auto';
      image.decoding = 'async';
    });
  }

  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ds-cart-upsell-add]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();
    this.#add(button);
  };

  async #add(button) {
    if (button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) {
      this.#fail('This product is currently unavailable.');
      return;
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.#setBusy(button, true);
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || 'Unable to add this product');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Unable to refresh cart');

      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: data.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'deepsweep-cart-upsell',
          didError: false,
        },
      });
      this.#announce('Recommended product added to your cart.');
    } catch (error) {
      deferred.reject(error);
      const message = error instanceof Error ? error.message : 'Unable to add this product';
      this.#fail(message);
      this.#setBusy(button, false);
    }
  }

  #setBusy(button, busy) {
    button.disabled = busy;
    button.toggleAttribute('aria-busy', busy);
    const label = button.querySelector('[data-ds-cart-upsell-label]');
    if (label) label.textContent = busy ? 'Adding…' : 'Add';
  }

  #announce(message) {
    const status = this.querySelector('[data-ds-cart-upsell-status]');
    if (status) status.textContent = message;
  }

  #fail(message) {
    this.#announce(message);
    this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
  }
}

if (!customElements.get('ds-cart-upsells')) {
  customElements.define('ds-cart-upsells', DeepSweepCartUpsells);
}
